import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";

/**
 * Change #100316 - the department topology as an org chart, after the AG
 * Charts organisation example Paul pointed at (that series is AG Charts
 * Enterprise, which we do not license, so this is the same shape drawn with
 * reactflow, already a dependency for the Automation builder).
 *
 * Top-down cards joined by step connectors. A card whose children are all
 * leaves stacks them in one indented column (AG's "compact" layout) so a
 * department of twelve technicians does not run off the side of the screen.
 * Every card with children can be collapsed; zoom and pan come from reactflow.
 *
 * The layout is computed here (a small tidy-tree pass) rather than pulling in
 * dagre/elk for one screen.
 */

const CARD_W = 230;
const CARD_H = 74;
const GAP_X = 28; // between sibling subtrees
const GAP_Y = 46; // parent row to child row
const STACK_INDENT = 28; // leaf column sits this far right of the spine
const STACK_GAP = 12; // between stacked leaves

const DEFAULT_BG = "#EEF2FF";
const DEFAULT_TEXT = "#4F46E5";

// ── layout ───────────────────────────────────────────────────────────────────

const visibleKids = (n, collapsed) =>
  collapsed.has(n.id) ? [] : n.children || [];

function measure(n, collapsed, cache) {
  const kids = visibleKids(n, collapsed);
  if (!kids.length) {
    cache.set(n.id, { w: CARD_W, cols: [] });
    return CARD_W;
  }
  kids.forEach((k) => measure(k, collapsed, cache));
  const leaves = kids.filter((k) => !visibleKids(k, collapsed).length);
  const branches = kids.filter((k) => visibleKids(k, collapsed).length);
  // Only use the stacked column when it actually saves width.
  const stack = leaves.length > 1 || (leaves.length && branches.length);
  const cols = [];
  if (stack) cols.push({ type: "stack", nodes: leaves, w: CARD_W + STACK_INDENT });
  (stack ? branches : kids).forEach((k) =>
    cols.push({ type: "node", node: k, w: cache.get(k.id).w })
  );
  const total =
    cols.reduce((s, c) => s + c.w, 0) + GAP_X * Math.max(0, cols.length - 1);
  const w = Math.max(CARD_W, total);
  cache.set(n.id, { w, cols, total });
  return w;
}

function place(n, x, y, collapsed, cache, out, parentId, viaLeft) {
  const m = cache.get(n.id);
  const nx = x + (m.w - CARD_W) / 2;
  out.nodes.push({
    id: n.id,
    type: n.kind,
    position: { x: nx, y },
    data: {
      ...n.data,
      hasKids: (n.children || []).length > 0,
      hiddenCount: collapsed.has(n.id) ? countDescendants(n) : 0,
      collapsed: collapsed.has(n.id),
    },
    draggable: false,
  });
  if (parentId) {
    out.edges.push({
      id: `${parentId}->${n.id}`,
      source: parentId,
      target: n.id,
      targetHandle: viaLeft ? "left" : "top",
      type: "smoothstep",
      pathOptions: { borderRadius: 8 },
      style: { stroke: "var(--bs-border-color, #cbd5e1)", strokeWidth: 1.5 },
    });
  }
  if (!m.cols.length) return;
  let cx = x + (m.w - m.total) / 2;
  const cy = y + CARD_H + GAP_Y;
  m.cols.forEach((col) => {
    if (col.type === "stack") {
      col.nodes.forEach((leaf, i) =>
        place(
          leaf,
          cx + STACK_INDENT,
          cy + i * (CARD_H + STACK_GAP),
          collapsed,
          cache,
          out,
          n.id,
          true
        )
      );
    } else {
      place(col.node, cx, cy, collapsed, cache, out, n.id, false);
    }
    cx += col.w + GAP_X;
  });
}

function countDescendants(n) {
  return (n.children || []).reduce((s, k) => s + 1 + countDescendants(k), 0);
}

function layoutForest(roots, collapsed) {
  const cache = new Map();
  const out = { nodes: [], edges: [] };
  let x = 0;
  roots.forEach((r) => {
    const w = measure(r, collapsed, cache);
    place(r, x, 0, collapsed, cache, out, null, false);
    x += w + GAP_X * 2;
  });
  return out;
}

// ── cards ────────────────────────────────────────────────────────────────────

const handleStyle = { opacity: 0, width: 1, height: 1, border: 0 };

function Expander({ data }) {
  if (!data.hasKids) return null;
  return (
    <button
      type="button"
      className="org-chart__expander nodrag"
      title={data.collapsed ? "Expand" : "Collapse"}
      onClick={(e) => {
        e.stopPropagation();
        data.onToggle?.(data.nodeId);
      }}
    >
      {data.collapsed ? `+${data.hiddenCount}` : "−"}
    </button>
  );
}

const DepartmentCard = memo(({ data }) => (
  <div
    className="org-chart__card org-chart__card--dept"
    style={{
      borderColor: data.textColour,
      background: data.bgColour,
      color: data.textColour,
    }}
    title={data.description || data.name}
  >
    <Handle type="target" position={Position.Top} id="top" style={handleStyle} />
    <Handle type="target" position={Position.Left} id="left" style={handleStyle} />
    <div className="org-chart__dept-icon">
      <i className="ri-team-line" />
    </div>
    <div className="org-chart__text">
      <div className="org-chart__name">{data.name}</div>
      <div className="org-chart__sub">
        {data.count} {data.count === 1 ? "person" : "people"}
        {data.flags?.map((f) => (
          <span key={f.label} className={`badge ms-1 ${f.className}`} title={f.title}>
            {f.label}
          </span>
        ))}
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="bottom" style={handleStyle} />
    <Expander data={data} />
  </div>
));

const PersonCard = memo(({ data }) => (
  <div
    className="org-chart__card org-chart__card--person"
    style={{ borderLeftColor: data.deptColour }}
    onClick={() => data.onOpen?.(data.staffId)}
    title={`Open ${data.name}`}
  >
    <Handle type="target" position={Position.Top} id="top" style={handleStyle} />
    <Handle type="target" position={Position.Left} id="left" style={handleStyle} />
    {data.imageUrl ? (
      <img src={data.imageUrl} alt="" className="org-chart__avatar" />
    ) : (
      <span className="org-chart__avatar org-chart__avatar--initial">
        {(data.name || "?").charAt(0).toUpperCase()}
      </span>
    )}
    <div className="org-chart__text">
      <div className="org-chart__name">{data.name}</div>
      <div className="org-chart__sub">{data.jobTitle || " "}</div>
      {data.isManager && (
        <span className="org-chart__tag" style={{ background: data.deptColour }}>
          Manager
        </span>
      )}
    </div>
    <Handle type="source" position={Position.Bottom} id="bottom" style={handleStyle} />
    <Expander data={data} />
  </div>
));

DepartmentCard.displayName = "DepartmentCard";
PersonCard.displayName = "PersonCard";

const nodeTypes = { dept: DepartmentCard, person: PersonCard };

// ── chart ────────────────────────────────────────────────────────────────────

function Chart({ roots, onOpenStaff, height }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const { fitView } = useReactFlow();

  const onToggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Callbacks ride on each node's data so the memoised cards can reach them.
  const wire = useCallback(
    (n) => ({
      ...n,
      data: { ...n.data, nodeId: n.id, onToggle, onOpen: onOpenStaff },
      children: (n.children || []).map(wire),
    }),
    [onToggle, onOpenStaff]
  );

  const { nodes, edges } = useMemo(
    () => layoutForest(roots.map(wire), collapsed),
    [roots, wire, collapsed]
  );

  // Re-frame after a collapse/expand or a data change.
  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 250 }), 30);
    return () => clearTimeout(t);
  }, [nodes.length, fitView]);

  const allIds = useMemo(() => {
    const ids = [];
    const walk = (n) => {
      if ((n.children || []).length) ids.push(n.id);
      (n.children || []).forEach(walk);
    };
    roots.forEach(walk);
    return ids;
  }, [roots]);

  return (
    <div className="org-chart" style={{ height }}>
      <div className="org-chart__toolbar">
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => setCollapsed(new Set())}
        >
          <i className="ri-expand-diagonal-line me-1" />
          Expand all
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => setCollapsed(new Set(allIds))}
        >
          <i className="ri-collapse-diagonal-line me-1" />
          Collapse all
        </button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesConnectable={false}
        nodesDraggable={false}
        elementsSelectable={false}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.1}
        maxZoom={1.75}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} color="#cbd5e1" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

export default function OrgChart(props) {
  return (
    <ReactFlowProvider>
      <Chart {...props} />
    </ReactFlowProvider>
  );
}

export { DEFAULT_BG, DEFAULT_TEXT };
